/**
 * Messages — the office's side of a two-way conversation with a member.
 *
 * Laid out as a queue and a conversation, not as an inbox: the default view is
 * what is waiting on a reply, because that is the only list that represents
 * work. Replying is the same action for whoever is on duty, and the reply goes
 * out as the club rather than as a named person, so a member is never left
 * waiting on one individual who is away.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { relative, shortDate, type Message, type MessageThread } from '@lcc/shared';
import { api } from '../lib/api';
import {
  Card, Empty, ErrorNote, Loading, Modal, Pill, SelectField, TextField,
  errorMessage, useToast,
} from '../components/ui';
import { PageHeader } from '../App';

type Filter = 'waiting' | 'open' | 'answered' | 'closed' | 'all';

const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: 'waiting', label: 'Waiting on a reply' },
  { value: 'open', label: 'Open' },
  { value: 'answered', label: 'Answered' },
  { value: 'closed', label: 'Closed' },
  { value: 'all', label: 'Everything' },
];

export function MessagesPage() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [filter, setFilter] = useState<Filter>('waiting');
  const [search, setSearch] = useState('');
  const [composing, setComposing] = useState(false);

  const threads = useQuery({
    queryKey: ['messages', 'threads', filter, search],
    queryFn: () => api.get<{ data: MessageThread[]; waiting: number; meta: { total: number } }>(
      `/messages/office/threads?state=${filter}&perPage=100`
      + (search ? `&q=${encodeURIComponent(search)}` : '')),
    refetchInterval: 60_000,
  });

  /*
   * The thread being read stays in the list even when it no longer matches the
   * filter — replying moves it out of "waiting", and a row that disappears
   * from under the cursor mid-conversation is disorienting.
   */
  const open = useQuery({
    queryKey: ['messages', 'thread', id],
    enabled: Boolean(id),
    queryFn: () => api.get<{ data: MessageThread }>(`/messages/${id}`).then((r) => r.data),
  });
  const listed = threads.data?.data ?? [];
  const rows = id && open.data && !listed.some((t) => t.id === id)
    ? [open.data, ...listed]
    : listed;

  return (
    <>
      <PageHeader
        title="Messages"
        sub="Conversations with members — questions in, answers out"
        actions={(
          <button className="btn btn--accent" type="button" onClick={() => setComposing(true)}>
            Message a member
          </button>
        )}
      />

      <div className="content">
        <div className="msg">
          <div className="msg__list">
            <div className="msg__filters">
              <SelectField
                label="Show"
                value={filter}
                onChange={(v) => setFilter(v as Filter)}
                options={FILTERS}
              />
              <TextField
                label="Search"
                value={search}
                onChange={setSearch}
                placeholder="Member, subject or words in a message"
              />
            </div>

            {threads.isLoading ? <Loading />
              : threads.error ? <ErrorNote error={threads.error} />
              : !rows.length
                ? (
                  <Card>
                    <Empty title={filter === 'waiting' ? 'Nothing waiting' : 'No conversations'}>
                      {filter === 'waiting'
                        ? 'Every member message has been answered.'
                        : 'Nothing matches that.'}
                    </Empty>
                  </Card>
                )
                : (
                  <ul className="msg__threads">
                    {rows.map((t) => (
                      <li key={t.id}>
                        <Link
                          to={`/messages/${t.id}`}
                          className={`msg__thread${t.id === id ? ' is-active' : ''}`}
                        >
                          <div className="msg__thread-top">
                            <b>{t.memberName}</b>
                            <span className="small muted">{relative(t.lastMessageAt)}</span>
                          </div>
                          <div className="msg__thread-subject">{t.subject}</div>
                          <div className="msg__thread-preview">{t.lastMessagePreview}</div>
                          <div className="msg__thread-tags">
                            {t.unreadForOffice > 0 && <Pill tone="bad">needs a reply</Pill>}
                            {t.state === 'closed' && <Pill>closed</Pill>}
                            {t.state === 'answered' && t.unreadForOffice === 0
                              && <Pill tone="good">answered</Pill>}
                            <span className="small muted">
                              {t.messageCount} message{t.messageCount === 1 ? '' : 's'}
                            </span>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
          </div>

          <div className="msg__pane">
            {id
              ? <Conversation id={id} onClosed={() => navigate('/messages')} />
              : (
                <Card>
                  <Empty title="Pick a conversation">
                    Choose one on the left, or start a new one with a member.
                  </Empty>
                </Card>
              )}
          </div>
        </div>
      </div>

      {composing && <ComposeModal onClose={() => setComposing(false)} />}
    </>
  );
}

/* --------------------------------------------------------- conversation --- */

function Conversation({ id, onClosed }: { id: string; onClosed: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [draft, setDraft] = useState('');
  const endRef = useRef<HTMLDivElement>(null);

  const thread = useQuery({
    queryKey: ['messages', 'thread', id],
    queryFn: () => api.get<{ data: MessageThread & { messages: Message[] } }>(`/messages/${id}`)
      .then((r) => r.data),
    refetchInterval: 30_000,
  });

  // Opening a thread marks it read, so the queue and its badge must be refetched.
  useEffect(() => {
    if (thread.data) void qc.invalidateQueries({ queryKey: ['messages', 'threads'] });
  }, [thread.data?.unreadForOffice, qc, thread.data]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'nearest' });
  }, [thread.data?.messages.length]);

  const reply = useMutation({
    mutationFn: (close: boolean) => api.post(`/messages/${id}/replies`, { body: draft, close }),
    onSuccess: (_r, close) => {
      setDraft('');
      void qc.invalidateQueries({ queryKey: ['messages'] });
      toast.success(close ? 'Replied and closed.' : 'Reply sent.');
      if (close) onClosed();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const reopen = useMutation({
    mutationFn: () => api.patch(`/messages/${id}`, { state: 'open' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['messages'] });
      toast.success('Reopened.');
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  if (thread.isLoading) return <Card><Loading /></Card>;
  if (thread.error) return <Card><ErrorNote error={thread.error} /></Card>;
  const t = thread.data!;

  return (
    <Card flush>
      <div className="msg__head">
        <div>
          <h2>{t.subject}</h2>
          <p className="small muted">
            <Link to={`/members/${t.memberId}`}>{t.memberName}</Link>
            {t.memberEmail ? ` · ${t.memberEmail}` : ''}
            {' · started '}{shortDate(t.createdAt)}
            {t.openedBy === 'office' ? ' by the office' : ' by the member'}
          </p>
        </div>
        {t.state === 'closed' && (
          <button className="btn btn--sm" type="button"
            disabled={reopen.isPending} onClick={() => reopen.mutate()}>
            Reopen
          </button>
        )}
      </div>

      <div className="msg__log">
        {t.messages.map((m) => (
          <div key={m.id} className={`bubble bubble--${m.author}`}>
            <div className="bubble__meta">
              <b>{m.authorName}</b>
              <span>{relative(m.createdAt)}</span>
            </div>
            <div className="bubble__body">{m.body}</div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      <div className="msg__compose">
        <textarea
          rows={4}
          value={draft}
          placeholder={t.state === 'closed'
            ? 'Replying reopens this conversation.'
            : `Reply to ${t.memberName.split(' ')[0]}…`}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            // Enter sends; Shift+Enter is a new line. A reply is usually one line.
            if (e.key === 'Enter' && !e.shiftKey && draft.trim()) {
              e.preventDefault();
              reply.mutate(false);
            }
          }}
        />
        <div className="msg__compose-actions">
          <p className="small muted">
            Sent as <b>Lindsay Curling Club</b>. The member gets a push notification.
          </p>
          <div className="row row--tight">
            <button className="btn" type="button"
              disabled={!draft.trim() || reply.isPending}
              onClick={() => reply.mutate(true)}>
              Reply &amp; close
            </button>
            <button className="btn btn--accent" type="button"
              disabled={!draft.trim() || reply.isPending}
              onClick={() => reply.mutate(false)}>
              {reply.isPending ? 'Sending…' : 'Send reply'}
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}

/* ------------------------------------------------------------- compose ---- */

function ComposeModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [memberId, setMemberId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');

  const members = useQuery({
    queryKey: ['members', 'search', search],
    queryFn: () => api.get<{ data: Array<{ id: string; fullName: string; email: string | null }> }>(
      `/members?perPage=25${search ? `&q=${encodeURIComponent(search)}` : ''}`),
  });

  const send = useMutation({
    mutationFn: () => api.post<{ data: MessageThread }>('/messages', { memberId, subject, body }),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['messages'] });
      toast.success('Message sent.');
      onClose();
      navigate(`/messages/${r.data.id}`);
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const chosen = members.data?.data.find((m) => m.id === memberId);

  return (
    <Modal
      title="Message a member"
      onClose={onClose}
      footer={(
        <>
          <button className="btn" type="button" onClick={onClose}>Cancel</button>
          <button
            className="btn btn--accent" type="button"
            disabled={!memberId || subject.trim().length < 3 || !body.trim() || send.isPending}
            onClick={() => send.mutate()}
          >
            {send.isPending ? 'Sending…' : chosen ? `Send to ${chosen.fullName}` : 'Send'}
          </button>
        </>
      )}
    >
      <TextField label="Find the member" value={search} onChange={setSearch}
        placeholder="Name or email address" />
      {members.isLoading ? <Loading /> : (
        <div className="picker">
          {members.data?.data.map((m) => (
            <button
              key={m.id} type="button"
              className={`picker__row${m.id === memberId ? ' is-on' : ''}`}
              onClick={() => setMemberId(m.id)}
            >
              <b>{m.fullName}</b>
              <span className="small muted">{m.email ?? 'no email on file'}</span>
            </button>
          ))}
        </div>
      )}
      <TextField label="Subject" value={subject} onChange={setSubject}
        placeholder="What it is about" />
      <div className="field">
        <label htmlFor="compose-body">Message</label>
        <textarea id="compose-body" rows={5} value={body}
          onChange={(e) => setBody(e.target.value)} />
      </div>
    </Modal>
  );
}
